import { useTranslation } from 'next-i18next';
import { ChemicalResistance, type Chemical } from '@igus/material-domain';
import { useState } from 'react';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';

export interface CompareMaterialsModalProps {
  chemicals: Chemical[];
  searchValue: string;
}

const partitionSize = 15;

export const MaterialChemicalResistance = ({ chemicals, searchValue }: CompareMaterialsModalProps) => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const [paritionLimit, setPartitionLimit] = useState(partitionSize);

  const chemicalResistance = {
    resistant: {
      label: 'RESISTANT',
      value: ChemicalResistance.resistant,
    },
    conditionallyResistant: {
      label: 'LIMITED_RESISTANT',
      value: ChemicalResistance.partlyResistant,
    },
    nonResistant: {
      label: 'NOT_RESISTANT',
      value: ChemicalResistance.notResistant,
    },
    notAvailable: {
      label: '"NO_DATA',
      value: ChemicalResistance.noData,
    },
  };

  const resistancePropertiesToDisplay = [
    chemicalResistance.resistant,
    chemicalResistance.conditionallyResistant,
    chemicalResistance.nonResistant,
  ];

  const table = () => (
    <table className="w-full">
      <thead>
        <th className="bg-white h-[56px] border-r border-b w-[25%]">
          {t('CHEMICALS.CHEMICALS', { ns: 'free-form-hub/chemicals' })}
        </th>
        {resistancePropertiesToDisplay.map((property, index) => (
          <th
            key={property.value}
            className={`px-2 w-[25%] border-r border-b whitespace-nowrap ${
              index % 2 === 0 ? 'bg-gray-50' : ''
            } sticky top-0 bg-white`}
          >
            <p className="text-sm font-normal">
              {t(`CHEMICAL_PROPERTIES.${property.label}`, { ns: 'free-form-hub/chemicals' })}
            </p>
          </th>
        ))}
      </thead>
      <tbody>
        {chemicals.slice(0, paritionLimit).map(chemical => (
          <tr key={chemical.name} className="border-t border-[#AFAFAF]">
            <td className="px-4 border-r text-sm w-[25%] sticky left-0 bg-white">
              {t(`CHEMICALS.${chemical.name.toUpperCase()}`, { ns: 'free-form-hub/chemicals' })}
            </td>
            {chemical.resistance === chemicalResistance.notAvailable.value ? (
              <td className="h-[56px] text-center text-[#727272] text-xs italic">
                {t('CHEMICAL_PROPERTIES.NO_DATA', { ns: 'free-form-hub/chemicals' })}
              </td>
            ) : (
              resistancePropertiesToDisplay.map((property, index) => (
                <td key={property.value} className={`h-[56px] w-[25%] ${index % 2 === 0 ? 'bg-gray-50' : ''}`}>
                  {chemical.resistance === property.value && (
                    <span
                      className="block w-[12px] h-[12px] bg-gray-800 rounded-full m-auto"
                      data-testid={`${property.label}_${chemical.name}_dot`}
                    />
                  )}
                </td>
              ))
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="w-full pt-16">
      <div className="mb-16">
        <div>{table()}</div>
        {!chemicals.length && !searchValue && (
          <div className="w-full mt-4 text-sm">
            {t('CHEMICAL_SELECTION.NO_CHEMICALS_FOUND', { ns: 'free-form-hub/chemicals' })}
          </div>
        )}
        {!chemicals.length && searchValue && (
          <div className="w-full mt-4 text-sm">
            {t('CHEMICAL_SELECTION.EMPTY_LIST', { ns: 'free-form-hub/chemicals' })}
          </div>
        )}
        {chemicals.length >= paritionLimit &&
          (paritionLimit === chemicals.length ? (
            <div
              onClick={() => setPartitionLimit(partitionSize)}
              className="text-center cursor-pointer space-x-2 text-[14px] mt-4"
            >
              <span className="text-igus-primary">
                {t('CHEMICAL_SELECTION.SHOW_LESS', { ns: 'free-form-hub/chemicals' })}
              </span>
              <span>
                <Icon size={7} color={IconColor.ORANGE} id={IconID.ARROW_UP} />
              </span>
            </div>
          ) : (
            <div
              onClick={() => setPartitionLimit(chemicals.length)}
              className="text-center cursor-pointer flex items-center justify-center space-x-2 text-[14px] mt-4"
            >
              <span className="text-igus-primary">
                {t('CHEMICAL_SELECTION.SHOW_MORE', { ns: 'free-form-hub/chemicals' })}
              </span>
              <span>
                <Icon size={7} color={IconColor.ORANGE} id={IconID.ARROW_DOWN} />
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};
